/**
 * 검토 모드용 역방향 매칭 맵.
 *
 * DSD 데이터 모델은 `DsdParsedCell.matchedIxdCells` 로 DSD → IXD 방향만 직접
 * 제공한다. 검토 모드에서는 IXD 셀(또는 헤더로 일괄 선택)을 골라 매칭되는
 * DSD 셀들을 하이라이팅해야 하므로 그 역방향 lookup 이 필요하다.
 *
 * `normalizeCellId` 로 IXD 키를 정규화해 axis 순서 차이 등을 흡수한다.
 * cellId 는 축만 다른 물리 셀끼리 충돌할 수 있어, metadata 가 있는 매치 ref 는
 * identity key(`makeCellIdentityKey`) 로만 등록한다 — 매칭되지 않은 충돌 fact 의
 * 셀을 클릭했을 때 cellId fallback 이 다른 fact 의 매칭을 끌어오지 않게 하기 위함.
 * cellId 키는 metadata 없는 ref(구버전 레코드) 전용 fallback 이다.
 * (identity key 는 JSON 문자열이라 cellId 키 공간과 충돌하지 않는다.)
 */

import { makeCellIdentityKey, normalizeCellId } from "@/utils/ixd-cell-model";
import type { DSDSubChunkInfo } from "@/apis/compare/route";

export interface DsdMatchEntry {
  /** 이 IXD 셀과 매칭된 DSD 셀 전부. 강조·dim 판정용. */
  dsdIds: Set<string>;
  /**
   * 이 IXD 셀을 자신의 정본으로 지목한 DSD 셀 — **그 지목이 가리킨 DL 별로** 나눈다.
   *
   * 백엔드가 주는 정본은 DSD → IXD 한 방향이라(`focus_cell`) 역방향은 그 지목을
   * 뒤집어 쓴다. 그런데 같은 fact 를 두 DL 이 태깅하면 여러 DSD 셀이 같은 cellId 를
   * 지목하므로, 지목이 함께 실어준 DL 로 나눠 둬야 클릭한 IXD 셀의 DL 로 고를 수
   * 있다. DL 을 모르는 지목은 `null` 키에 들어가고, DL 로 못 고를 때의 폴백이 된다.
   */
  properDsdIdByDlViewId: Map<string | null, string>;
}

/** 정규화된 IXD cellId 또는 identity key → 매칭되는 DSD cell 들 */
export const buildDsdByIxdCellIdMap = (
  subChunks: DSDSubChunkInfo[] | undefined | null,
): Map<string, DsdMatchEntry> => {
  const map = new Map<string, DsdMatchEntry>();
  if (!subChunks?.length) return map;

  const pushKey = (
    key: string,
    dsdId: string,
    properDlViewId: string | null | undefined,
  ) => {
    let entry = map.get(key);
    if (!entry) {
      entry = { dsdIds: new Set(), properDsdIdByDlViewId: new Map() };
      map.set(key, entry);
    }
    entry.dsdIds.add(dsdId);
    if (properDlViewId === undefined) return;
    // 같은 DL 안에서 여럿이 같은 IXD 셀을 지목하면 문서 순서로 앞선 쪽을 남긴다.
    if (!entry.properDsdIdByDlViewId.has(properDlViewId)) {
      entry.properDsdIdByDlViewId.set(properDlViewId, dsdId);
    }
  };

  for (const subChunk of subChunks) {
    for (const block of subChunk.parsedContent ?? []) {
      if (block.blockType !== "table" || !block.cells) continue;
      for (const row of block.cells) {
        for (const cell of row) {
          if (!cell || !cell.isOrigin) continue;
          const proper = cell.mostProperCell;
          for (const matched of cell.matchedIxdCells ?? []) {
            if (!matched?.cellId) continue;
            // 정본이 아닌 매칭은 DL 인자를 안 넘겨 지목으로 등재되지 않는다.
            const properDlViewId =
              matched.cellId === proper?.cellId
                ? (proper.dlViewId ?? null)
                : undefined;
            if (matched.metadata) {
              pushKey(
                makeCellIdentityKey(matched.metadata),
                cell.dsdId,
                properDlViewId,
              );
            } else {
              pushKey(
                normalizeCellId(matched.cellId),
                cell.dsdId,
                properDlViewId,
              );
            }
          }
        }
      }
    }
  }
  return map;
};
